"use client";

import { useEffect, useRef, useState } from "react";

import { cn } from "../lib/utils";
import { isIconoirIconName, type IconoirIconName } from "../lib/iconoirIcons";

interface IconSuggestionsProps {
  /**
   * Cache key and fetch trigger: suggestions are refetched whenever this
   * changes, and reused when it doesn't. Callers should update it only when
   * the input has settled (e.g. on blur), not on every keystroke. An empty
   * string means "nothing to go on" and hides the row entirely.
   */
  input: string;
  /** Fetches suggested icon names. Supplied by the caller so this package
   * stays free of API-client wiring, matching `AITextAreaInput`'s `onSuggest`. */
  onSuggest: () => Promise<string[]>;
  /** Currently selected icon, so the row can show which suggestion is active. */
  value: string | null;
  onChange: (value: IconoirIconName | null) => void;
}

/**
 * A row of AI-suggested icons above the full picker. Entirely absent until
 * there is something to suggest from, and silent on failure — this is a
 * shortcut to the picker below it, never a blocker.
 */
export function IconSuggestions({
  input,
  onSuggest,
  value,
  onChange,
}: IconSuggestionsProps) {
  const [icons, setIcons] = useState<IconoirIconName[]>([]);
  const [loading, setLoading] = useState(false);
  // Suggestions are keyed by input, so reopening a dialog or re-blurring an
  // unchanged field costs nothing.
  const cache = useRef(new Map<string, IconoirIconName[]>());
  const [reloadCount, setReloadCount] = useState(0);

  useEffect(() => {
    // Both early returns clear `loading` themselves: a run that resolves from
    // the cache may be superseding an in-flight fetch whose `.finally` is
    // skipped (cleanup already flipped its `active` to false), and nobody else
    // would ever turn the skeleton back off.
    if (!input) {
      setIcons([]);
      setLoading(false);
      return;
    }

    const cached = cache.current.get(input);
    if (cached) {
      setIcons(cached);
      setLoading(false);
      return;
    }

    let active = true;
    setLoading(true);
    onSuggest()
      .then((suggested) => {
        if (!active) {
          return;
        }
        // The API filters to what we offered, but this component renders the
        // names directly as CSS classes, so check again before trusting them.
        const usable = suggested.filter(isIconoirIconName);
        cache.current.set(input, usable);
        setIcons(usable);
      })
      .catch(() => {
        if (active) {
          setIcons([]);
        }
      })
      .finally(() => {
        if (active) {
          setLoading(false);
        }
      });

    return () => {
      active = false;
    };
    // `onSuggest` closes over current form values and changes identity every
    // render; `input` is the intended trigger.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [input, reloadCount]);

  // Nothing to suggest from, or nothing came back — the picker below stands
  // on its own, so show nothing at all.
  if (!input || (!loading && icons.length === 0)) {
    return null;
  }

  return (
    <div>
      <div className="mb-1 flex items-center justify-between">
        <span className="flex items-center gap-1.5 text-sm font-medium text-gray-700">
          <span className="iconoir-sparks text-base" aria-hidden="true" />
          Suggested icons
        </span>
        <button
          type="button"
          onClick={() => {
            cache.current.delete(input);
            setReloadCount((count) => count + 1);
          }}
          disabled={loading}
          className="text-xs text-gray-500 hover:text-blue-500 disabled:cursor-not-allowed disabled:opacity-40"
        >
          Refresh
        </button>
      </div>
      <div
        role="radiogroup"
        aria-label="Suggested icons"
        aria-busy={loading}
        className="flex gap-1 rounded-md bg-white p-2 shadow-sm ring-1 ring-inset ring-gray-300"
      >
        {loading
          ? Array.from({ length: 5 }, (_, index) => (
              <span
                key={index}
                aria-hidden="true"
                className="h-9 w-9 animate-pulse rounded-md bg-gray-100"
              />
            ))
          : icons.map((icon) => {
              const selected = value === icon;
              return (
                <button
                  key={icon}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  aria-label={icon}
                  title={icon}
                  onClick={() => onChange(selected ? null : icon)}
                  className={cn(
                    "flex h-9 w-9 items-center justify-center rounded-md text-gray-600 hover:bg-gray-100",
                    selected &&
                      "bg-blue-500/10 text-blue-500 ring-1 ring-blue-500",
                  )}
                >
                  <span
                    className={`iconoir-${icon} text-xl`}
                    aria-hidden="true"
                  />
                </button>
              );
            })}
      </div>
    </div>
  );
}
